import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Sparkles, Bot, Cloud, Database, ShieldCheck,
  Cpu, GraduationCap, Trophy, Lock, Zap, Star, TrendingUp, Lightbulb,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { getCertAccess } from '@/lib/certAccess';

const CERTS = [
  {
    code: 'ai-900',
    name: 'AI-900',
    title: 'Azure AI Fundamentals',
    level: 'Fundamentals',
    levelColor: 'bg-emerald-100 text-emerald-700',
    accent: 'from-emerald-500 to-teal-600',
    icon: Sparkles,
    href: '/ai-900-exam-prep',
    price: 39,
    questions: 250,
    duration: '60 min',
    blurb: 'Cloud AI workloads, ML basics, computer vision, NLP, and Generative AI concepts.',
    skills: ['ML fundamentals', 'Computer vision', 'NLP', 'Generative AI', 'Responsible AI'],
  },
  {
    code: 'az-900',
    name: 'AZ-900',
    title: 'Azure Fundamentals',
    level: 'Fundamentals',
    levelColor: 'bg-emerald-100 text-emerald-700',
    accent: 'from-sky-500 to-blue-600',
    icon: Cloud,
    href: '/az-900-exam-prep',
    price: 39,
    questions: 250,
    duration: '60 min',
    blurb: 'Core cloud concepts, Azure architecture, services, governance and compliance.',
    skills: ['Cloud concepts', 'Azure services', 'Cost management', 'Governance', 'SLAs'],
  },
  {
    code: 'ab-900',
    name: 'AB-900',
    title: 'Copilot & Agent Admin Fundamentals',
    level: 'Fundamentals',
    levelColor: 'bg-emerald-100 text-emerald-700',
    accent: 'from-purple-500 to-fuchsia-600',
    icon: Bot,
    href: '/ab-900-exam-prep',
    price: 39,
    questions: 250,
    duration: '45 min',
    blurb: 'Microsoft 365 Copilot governance, agent administration, and Purview data protection.',
    skills: ['M365 Copilot', 'Purview DLP', 'Agent admin', 'Sensitivity labels', 'PAYG licensing'],
  },
  {
    code: 'ai-102',
    name: 'AI-102',
    title: 'Azure AI Engineer Associate',
    level: 'Associate',
    levelColor: 'bg-amber-100 text-amber-700',
    accent: 'from-violet-500 to-indigo-600',
    icon: Cpu,
    href: '/ai-102-exam-prep',
    price: 39,
    questions: 250,
    duration: '100 min',
    blurb: 'Implement Azure AI solutions: generative AI, computer vision, NLP, knowledge mining, agentic.',
    skills: ['Azure OpenAI', 'AI Foundry', 'Vision/NLP', 'Agents', 'AI Search'],
  },
  {
    code: 'dp-100',
    name: 'DP-100',
    title: 'Azure Data Scientist Associate',
    level: 'Associate',
    levelColor: 'bg-amber-100 text-amber-700',
    accent: 'from-rose-500 to-pink-600',
    icon: Database,
    href: '/dp-100-exam-prep',
    price: 39,
    questions: 250,
    duration: '100 min',
    blurb: 'Design, train, deploy & retrain ML models on Azure Machine Learning.',
    skills: ['Azure ML', 'AutoML', 'MLflow', 'Pipelines', 'Endpoints'],
  },
  {
    code: 'sc-300',
    name: 'SC-300',
    title: 'Identity and Access Administrator',
    level: 'Associate',
    levelColor: 'bg-amber-100 text-amber-700',
    accent: 'from-indigo-500 to-blue-700',
    icon: ShieldCheck,
    href: '/sc-300-exam-prep',
    price: 49,
    questions: 250,
    duration: '115 min',
    blurb: 'Microsoft Entra: identities, Conditional Access, Identity Protection, PIM, governance.',
    skills: ['Entra ID', 'Conditional Access', 'Identity Protection', 'PIM', 'Identity Governance'],
  },
  {
    code: 'az-104',
    name: 'AZ-104',
    title: 'Microsoft Azure Administrator',
    level: 'Associate',
    levelColor: 'bg-amber-100 text-amber-700',
    accent: 'from-blue-500 to-cyan-600',
    icon: Cloud,
    href: '/az-104-exam-prep',
    price: 49,
    questions: 250,
    duration: '100 min',
    blurb: 'Manage Azure identities & governance, compute, storage, networking, and monitoring at scale.',
    skills: ['RBAC + Policy', 'VMs + VMSS', 'Storage tiers', 'VNet + NSG', 'Monitor + Backup'],
  },
];

const TRACK_GROUPS = [
  {
    label: 'Phase 1 — Fundamentals',
    color: 'border-emerald-300',
    summary: 'Build conceptual fluency. Three exam-grade fundamentals in ~3 weeks.',
    certs: ['ai-900', 'az-900', 'ab-900'],
    bundlePrice: 99,
    fullPrice: 117,
  },
  {
    label: 'Phase 2 — Associate (AI / Data)',
    color: 'border-amber-300',
    summary: 'Go hands-on with Azure AI and ML engineering tracks.',
    certs: ['ai-102', 'dp-100'],
    bundlePrice: 69,
    fullPrice: 78,
  },
  {
    label: 'Phase 3 — Associate (Cloud & Identity)',
    color: 'border-indigo-300',
    summary: 'Lock down identities and run Azure at scale — the linchpin of every cloud rollout.',
    certs: ['sc-300', 'az-104'],
    bundlePrice: 79,
    fullPrice: 98,
  },
];

const FULL_BUNDLE_FULL_PRICE = CERTS.reduce((s, c) => s + c.price, 0); // 293
const FULL_BUNDLE_NUDGE_PRICE = 199; // displayed as a future bundle target

export default function MicrosoftTrackPage() {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState([]);
  const email = user?.email || (typeof window !== 'undefined' ? localStorage.getItem('paywall_email') : '');

  useEffect(() => {
    let cancelled = false;
    if (email) {
      getCertAccess(email).then((data) => {
        if (!cancelled) setUnlocked(data.unlocked || []);
      });
    }
    return () => { cancelled = true; };
  }, [email]);

  const owned = unlocked.length;
  const remaining = CERTS.filter((c) => !unlocked.includes(c.code));
  const remainingTotal = remaining.reduce((s, c) => s + c.price, 0);

  return (
    <div className="min-h-screen bg-white" data-testid="microsoft-track-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 text-white pt-28 pb-20">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(99,102,241,0.4) 0px, transparent 50%), radial-gradient(circle at 80% 60%, rgba(56,189,248,0.35) 0px, transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-white/10 text-white border-white/20 mb-6 hover:bg-white/15">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> 7-Cert Microsoft Track · 1750+ exam-grade questions
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
            From <span className="text-emerald-300">AI Fundamentals</span> to{' '}
            <span className="text-sky-300">Identity Architect</span> — your Microsoft journey, sequenced.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            A guided path through 7 Microsoft certifications that turn engineers, admins, and analysts into
            production-ready cloud + AI + identity professionals. Each cert: $39-$49, 2 months access, AI Coach included.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#track">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold" data-testid="hero-view-track-btn">
                See the 7-cert roadmap <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/ai-900-exam-prep">
              <Button size="lg" variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white/10" data-testid="hero-start-free-btn">
                Start free with AI-900
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl">
            {[
              { v: '7', l: 'Certifications' },
              { v: '1750+', l: 'Exam Questions' },
              { v: '$39-49', l: 'Per cert · lifetime' },
              { v: 'GPT-4o', l: 'AI Coach 1:1' },
            ].map((s) => (
              <div key={s.l} className="rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur">
                <div className="text-2xl font-bold">{s.v}</div>
                <div className="text-xs text-slate-300 uppercase tracking-wide">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRESS NUDGE */}
      {email && (
        <section className="border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Trophy className="w-6 h-6 text-amber-500" />
              <div>
                <p className="text-sm text-slate-500">Signed in as <span className="font-medium text-slate-700">{email}</span></p>
                <p className="text-base font-semibold text-slate-900">
                  {owned} of {CERTS.length} certifications unlocked
                </p>
              </div>
            </div>
            {remaining.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">Complete the track for ${remainingTotal}</span>
                <Link to={remaining[0].href}>
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="track-continue-btn">
                    Continue with {remaining[0].name} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
            {remaining.length === 0 && (
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">All 7 certs unlocked — congrats!</Badge>
            )}
          </div>
        </section>
      )}

      {/* CERT GRID */}
      <section id="track" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <Badge variant="outline" className="mb-3"><GraduationCap className="w-3.5 h-3.5 mr-1" /> The full track</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Six certs, one production-ready career path</h2>
            <p className="mt-3 text-slate-600">
              Sequenced by Microsoft skill levels — each cert is independently usable, but the path is built so each
              builds on the last. Recommended order is left → right.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CERTS.map((c, idx) => {
              const isUnlocked = unlocked.includes(c.code);
              const Icon = c.icon;
              return (
                <Card key={c.code} className="group relative overflow-hidden border-slate-200 hover:shadow-lg hover:border-slate-300 transition-all" data-testid={`track-cert-card-${c.code}`}>
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${c.accent}`} />
                  <CardHeader className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center shadow-md`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={`${c.levelColor} hover:${c.levelColor}`}>{c.level}</Badge>
                        <span className="text-xs text-slate-400">Step {idx + 1} / {CERTS.length}</span>
                      </div>
                    </div>
                    <div>
                      <CardTitle className="text-xl text-slate-900">{c.name}</CardTitle>
                      <CardDescription className="text-base text-slate-700 font-medium">{c.title}</CardDescription>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-slate-600">{c.blurb}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {c.skills.slice(0, 4).map((s) => (
                        <Badge key={s} variant="outline" className="text-xs font-normal text-slate-600">{s}</Badge>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-100 pt-4">
                      <span><span className="font-semibold text-slate-700">{c.questions}+</span> questions</span>
                      <span>{c.duration} exam</span>
                    </div>
                    <div className="flex items-center justify-between pt-1">
                      <div>
                        {isUnlocked ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-sm">
                            <CheckCircle2 className="w-4 h-4" /> Unlocked
                          </span>
                        ) : (
                          <span className="text-slate-900 font-bold text-2xl">${c.price}</span>
                        )}
                        {!isUnlocked && <span className="text-xs text-slate-400 ml-1">one-time</span>}
                      </div>
                      <Link to={c.href}>
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`track-open-${c.code}`}>
                          {isUnlocked ? 'Open' : 'Start'} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* PHASED ROADMAP */}
      <section className="py-20 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <Badge variant="outline" className="mb-3"><TrendingUp className="w-3.5 h-3.5 mr-1" /> Suggested cadence</Badge>
            <h2 className="text-3xl font-bold text-slate-900">Three phases, ~10 weeks of focused study</h2>
            <p className="mt-3 text-slate-600">
              Each phase ends with a milestone you can put on a CV. Stack them, or take them one at a time.
            </p>
          </div>

          <div className="space-y-6">
            {TRACK_GROUPS.map((g) => {
              const groupCerts = g.certs.map((id) => CERTS.find((c) => c.code === id)).filter(Boolean);
              const groupOwned = groupCerts.filter((c) => unlocked.includes(c.code)).length;
              return (
                <Card key={g.label} className={`border-l-4 ${g.color}`}>
                  <CardContent className="p-6 grid lg:grid-cols-3 gap-6 items-center">
                    <div className="lg:col-span-1">
                      <h3 className="text-xl font-bold text-slate-900">{g.label}</h3>
                      <p className="text-sm text-slate-600 mt-1">{g.summary}</p>
                      <div className="mt-3 text-sm text-slate-500">
                        Progress: <span className="font-semibold text-slate-700">{groupOwned}/{groupCerts.length}</span> certs unlocked
                      </div>
                    </div>
                    <div className="lg:col-span-1 flex flex-wrap gap-2">
                      {groupCerts.map((c) => {
                        const isUnlocked = unlocked.includes(c.code);
                        return (
                          <Link key={c.code} to={c.href} className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-1.5 transition-colors ${isUnlocked ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-200 hover:border-slate-300 text-slate-700'}`}>
                            <c.icon className="w-4 h-4" /> {c.name}
                            {isUnlocked && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                          </Link>
                        );
                      })}
                    </div>
                    <div className="lg:col-span-1 lg:text-right">
                      <div className="inline-flex flex-col gap-1 items-start lg:items-end">
                        <span className="text-xs uppercase tracking-wide text-slate-400">Phase total</span>
                        <span className="text-2xl font-bold text-slate-900">${g.fullPrice}</span>
                        {g.bundlePrice && (
                          <span className="text-xs text-emerald-700">Bundle pricing coming soon ${g.bundlePrice}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Bundle teaser */}
          <Card className="mt-10 bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white border-0 overflow-hidden relative" data-testid="full-bundle-card">
            <div className="absolute -top-12 -right-12 w-56 h-56 bg-white/10 rounded-full blur-3xl" />
            <CardContent className="p-8 lg:p-10 relative">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div className="max-w-xl">
                  <Badge className="bg-white/20 hover:bg-white/25 mb-3"><Lock className="w-3 h-3 mr-1" /> Coming soon · save ~30%</Badge>
                  <h3 className="text-2xl lg:text-3xl font-bold">The full 7-cert track, bundled</h3>
                  <p className="mt-2 text-white/85 text-sm lg:text-base">
                    All seven Microsoft certifications + AI Coach + 2 months access in a single SKU. Targeting{' '}
                    <span className="font-bold text-white">${FULL_BUNDLE_NUDGE_PRICE}</span> vs.{' '}
                    <span className="line-through text-white/70">${FULL_BUNDLE_FULL_PRICE}</span> if purchased individually.
                  </p>
                </div>
                <div className="flex flex-col gap-3 w-full lg:w-auto">
                  <Link to="/contact">
                    <Button size="lg" className="bg-white text-indigo-700 hover:bg-white/90 font-semibold w-full lg:w-auto" data-testid="bundle-waitlist-btn">
                      Join the bundle waitlist <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/ai-900-exam-prep">
                    <Button size="lg" variant="ghost" className="text-white hover:bg-white/10 w-full lg:w-auto">
                      Or start with AI-900 free
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* WHY THIS TRACK */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <Badge variant="outline" className="mb-3"><Lightbulb className="w-3.5 h-3.5 mr-1" /> Why this sequence</Badge>
            <h2 className="text-3xl font-bold text-slate-900">Built around the actual jobs Microsoft hiring managers post</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Sequenced for momentum', body: 'Start with AI/Azure/M365 fundamentals to build confidence, then go deep into AI engineering, data science, and identity in associate-level certs.' },
              { icon: ShieldCheck, title: 'Identity is the new perimeter', body: 'Adding SC-300 means you can deploy AI Copilot, Azure ML workloads, AND govern who accesses them — exactly what enterprises buy.' },
              { icon: Star, title: 'Every cert ships with AI Coach', body: 'Stuck on a question? Ask GPT-4o for a 1:1 explanation. Unlimited messages on every Pro-unlocked cert.' },
            ].map((b) => (
              <Card key={b.title} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center mb-4">
                    <b.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">{b.title}</h3>
                  <p className="text-sm text-slate-600 mt-2">{b.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold">Ready to start the track?</h2>
          <p className="mt-3 text-slate-300">Pick any cert below — no credit card required to start.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {CERTS.map((c) => (
              <Link key={c.code} to={c.href}>
                <Button variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white/10" data-testid={`cta-cert-${c.code}`}>
                  <c.icon className="w-4 h-4 mr-2" /> {c.name}
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
